import {
  Button,
  Flex,
  IconButton,
  Input,
  NativeSelectField,
  NativeSelectRoot,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { FiTrash2 } from "react-icons/fi"

import {
  type ApiError,
  type QuizTemplatePublic,
  type QuizTemplateSettings,
  QuizTemplatesService,
} from "@/client"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import type {
  QuizSettings,
  QuizTemplateSettingsForBuilder,
} from "./QuizBuilder/QuizBuilderContext"

interface QuizTemplateControlsProps {
  currentSettings: QuizSettings
  buildTemplateSettingsPayload: () => QuizTemplateSettingsForBuilder
  onApplyTemplateSettings: (settings: QuizTemplateSettingsForBuilder) => void
}

function toBuilderTemplateSettings(
  settings: QuizTemplateSettings,
): QuizTemplateSettingsForBuilder {
  return {
    publish_date: null,
    cutoff_date: null,
    time_limit_minutes: settings.time_limit_minutes ?? null,
    allow_retake: settings.allow_retake ?? false,
    show_result_mode: settings.show_result_mode ?? "immediate",
    result_date: null,
    show_percentile: settings.show_percentile ?? false,
    show_score_comparison: settings.show_score_comparison ?? false,
    show_peer_performance: settings.show_peer_performance ?? false,
    allow_student_timer: settings.allow_student_timer ?? true,
    allow_mpl_transmutation: settings.allow_mpl_transmutation ?? false,
    show_rationale_mode: settings.show_rationale_mode ?? "after_all",
    tags: settings.tags ?? [],
  }
}

export function QuizTemplateControls({
  currentSettings,
  buildTemplateSettingsPayload,
  onApplyTemplateSettings,
}: QuizTemplateControlsProps) {
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const [selectedTemplateId, setSelectedTemplateId] = useState("")
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [templateName, setTemplateName] = useState("")
  const [templateDescription, setTemplateDescription] = useState("")

  const { data } = useQuery({
    queryKey: ["quiz-templates"],
    queryFn: () => QuizTemplatesService.listQuizTemplates(),
  })

  const templates = data?.data ?? []
  const selectedTemplate = useMemo(
    () =>
      templates.find(
        (template: QuizTemplatePublic) => template.id === selectedTemplateId,
      ) ?? null,
    [selectedTemplateId, templates],
  )

  const createTemplateMutation = useMutation({
    mutationFn: () =>
      QuizTemplatesService.createQuizTemplate({
        requestBody: {
          name: templateName.trim(),
          description: templateDescription.trim() || null,
          settings: buildTemplateSettingsPayload(),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quiz-templates"] })
      setTemplateName("")
      setTemplateDescription("")
      setIsSaveDialogOpen(false)
      showSuccessToast("Quiz template saved")
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId: string) =>
      QuizTemplatesService.deleteQuizTemplate({ templateId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quiz-templates"] })
      setSelectedTemplateId("")
      setIsDeleteDialogOpen(false)
      showSuccessToast("Quiz template deleted")
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const willKeepDates = Boolean(
    currentSettings.publish_date ||
      currentSettings.cutoff_date ||
      currentSettings.result_date,
  )

  const handleConfirmApply = () => {
    if (!selectedTemplate) {
      return
    }
    onApplyTemplateSettings(
      toBuilderTemplateSettings(selectedTemplate.settings),
    )
    setIsApplyDialogOpen(false)
  }

  return (
    <>
      <VStack gap={4} align="stretch">
        <Field label="Template">
          <Flex gap={3} wrap="wrap" align="end">
            <NativeSelectRoot flex="1" minW="220px">
              <NativeSelectField
                aria-label="Template"
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
              >
                <option value="">Choose a template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
            <Button
              onClick={() => setIsApplyDialogOpen(true)}
              disabled={!selectedTemplate}
            >
              Apply
            </Button>
            <Button onClick={() => setIsSaveDialogOpen(true)}>
              Save current settings as template
            </Button>
            <IconButton
              aria-label="Delete template"
              variant="ghost"
              colorPalette="red"
              disabled={!selectedTemplate}
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <FiTrash2 />
            </IconButton>
          </Flex>
        </Field>
      </VStack>

      <DialogRoot
        open={isSaveDialogOpen}
        onOpenChange={({ open }) => setIsSaveDialogOpen(open)}
      >
        <DialogContent maxW="md">
          <DialogHeader>
            <DialogTitle>Save quiz template</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <VStack gap={4} align="stretch">
              <Field label="Template name">
                <Input
                  value={templateName}
                  onChange={(event) => setTemplateName(event.target.value)}
                />
              </Field>
              <Field label="Description" optionalText="Optional">
                <Textarea
                  value={templateDescription}
                  onChange={(event) =>
                    setTemplateDescription(event.target.value)
                  }
                />
              </Field>
            </VStack>
          </DialogBody>
          <DialogFooter gap={3}>
            <Button variant="ghost" onClick={() => setIsSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createTemplateMutation.mutate()}
              disabled={!templateName.trim()}
              loading={createTemplateMutation.isPending}
            >
              Save template
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      <DialogRoot
        open={isApplyDialogOpen}
        onOpenChange={({ open }) => setIsApplyDialogOpen(open)}
      >
        <DialogContent maxW="lg">
          <DialogHeader>
            <DialogTitle>Apply template</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <VStack align="stretch" gap={4}>
              <Text>
                Apply "{selectedTemplate?.name}"? The current
                template-controlled quiz settings will be updated.
              </Text>
              <Text fontWeight="semibold">This will replace:</Text>
              <Text>
                Timing, retake behavior, result visibility, review settings, MPL
                transmutation, and tags.
              </Text>
              <Text fontWeight="semibold">This will not change:</Text>
              <Text>
                Quiz name, schedule dates, questions, sections, and program
                assignments.
              </Text>
              {willKeepDates && (
                <Text color="gray.700">
                  Existing publish, cutoff, and result release dates will be
                  kept.
                </Text>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter gap={3}>
            <Button variant="ghost" onClick={() => setIsApplyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmApply}>Apply template</Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      <DialogRoot
        open={isDeleteDialogOpen}
        onOpenChange={({ open }) => setIsDeleteDialogOpen(open)}
      >
        <DialogContent maxW="md">
          <DialogHeader>
            <DialogTitle>Delete template</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Text>
              Delete "{selectedTemplate?.name}"? Existing quizzes will not
              change.
            </Text>
          </DialogBody>
          <DialogFooter gap={3}>
            <Button
              variant="ghost"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              colorPalette="red"
              onClick={() => {
                if (selectedTemplate) {
                  deleteTemplateMutation.mutate(selectedTemplate.id)
                }
              }}
              loading={deleteTemplateMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </>
  )
}
